/**
 * THE ONLY MODULE THAT NAMES ms-agents RESPONSE FIELDS.
 *
 * Its sibling `broadcastPayload.js` owns the other direction: request fields.
 * Between them they are the entire snake_case surface of the Broadcasts
 * feature. Everything downstream — list, composer, detail, analytics — reads
 * the camelCase shapes returned from here and never touches a raw record. If
 * you find yourself typing `notification_title`, `link_clicked_count`,
 * `by_channel`, `unique_openers` or `audience_count` in a component, a hook or
 * a service, it belongs in this file instead.
 *
 * The tolerance tricks here (multi-key `pick`, envelope unwrapping,
 * case-insensitive status) are deliberate; collapsing a missing counter to
 * zero is not — an absent number stays absent.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THAT MATTERS MOST
 * ---------------------------------------------------------------------------
 * AN ABSENT NUMERIC DECODES TO `null`. A PRESENT ZERO DECODES TO `0`.
 *
 * A scheduled broadcast has no counters yet. The UI must render "—" for those
 * cells, never "0": zero is a measured result and absence is the absence of
 * one, and a dashboard that shows "0 delivered" for a send that has not
 * started is lying to the person reading it. Every numeric in this file goes
 * through `num()`, which returns null for absent and Number() for present.
 * There is no defaulting-to-zero anywhere below, and the test asserts that by
 * grepping this file's own source.
 *
 * The same rule applies one level up, to whole blocks: a push-only broadcast
 * decodes `email: null`, not an email block full of zeros. Null means "this
 * channel was never targeted"; zeros would mean "we targeted it and reached
 * nobody", which is a different and much worse story.
 *
 * PURE — no React, no localStorage, no `Date.now()`, no network. Formatting an
 * instant for display is the caller's job; this file hands back the raw ISO
 * string it was given.
 */

/* ── Primitives ───────────────────────────────────────────────────────── */

/**
 * Read the first present value among several server naming conventions.
 *
 * @param {Object} record
 * @param {string[]} keys
 * @returns {*} undefined when no key carries a value.
 */
const pick = (record, keys) => {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

/**
 * THE helper. Absent → null, present → Number.
 *
 * Empty string counts as absent: some serializers emit "" for an unpopulated
 * column, and Number("") is 0, which is the exact lie this file exists to
 * prevent.
 *
 * @param {*} value
 * @returns {number|null}
 */
const num = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Absent → null, present → String. Never the empty string, so the grid can
 * treat "nothing here" as one case rather than two.
 *
 * @param {*} value
 * @returns {string|null}
 */
const text = (value) => {
  if (value === undefined || value === null) return null;
  const string = String(value).trim();
  return string === "" ? null : string;
};

/** Lowercased text, for closed enums the server may send in either casing. */
const slug = (value) => {
  const string = text(value);
  return string === null ? null : string.toLowerCase();
};

/** Strings the wire uses for false. JSON should not need this; CSV exports do. */
const FALSE_WORDS = new Set(["false", "0", "no", "null"]);

/**
 * Coerce to a real boolean. `Boolean("false")` is true, which is how a
 * retracted flag round-tripped through a query string reads as retracted.
 *
 * @param {*} value
 * @returns {boolean}
 */
const bool = (value) => {
  if (typeof value === "string") return !FALSE_WORDS.has(value.trim().toLowerCase());
  return Boolean(value);
};

/**
 * Channel selection: a scalar on most records, an array on a few.
 * @returns {string|string[]|null}
 */
const channelsOf = (raw) => {
  if (Array.isArray(raw)) return raw.map((entry) => String(entry).trim().toLowerCase());
  return slug(raw);
};

/** Pull a list out of whichever envelope the backend used. */
const unwrapList = (payload, primaryKey) => {
  if (Array.isArray(payload)) return payload;
  const list =
    payload?.[primaryKey] || payload?.items || payload?.results || payload?.data;
  return Array.isArray(list) ? list : [];
};

/** `total` under either of its two names. */
const totalOf = (payload) => num(pick(payload, ["total", "count"]));

/* ── Broadcast row ────────────────────────────────────────────────────── */

/**
 * One broadcast, as the list and the detail header render it.
 *
 * @param {Object} record
 * @returns {Object}
 */
const rowFromRecord = (record = {}) => ({
  id: text(pick(record, ["id"])),
  // Records written by the old Notifications composer carry `title` only.
  title: text(pick(record, ["notification_title", "title"])),
  // The description. The list grid never shows it, but the detail header and
  // `draftFromRow` both need it, and decoding it here keeps that need from
  // leaking a second wire name into another module.
  body: text(pick(record, ["notification_body", "message"])),
  // The email body. Only `draftFromRow` reads it — an email restored into the
  // composer without it would open with its subject and preview line intact
  // and its actual message gone, which is worse than not restoring at all.
  emailBodyHtml: text(record.email_body_html),
  channels: channelsOf(record.channels),
  pushSegment: slug(record.push_segment),
  emailSegment: slug(record.email_segment),
  routeKey: slug(record.route_key),
  status: slug(record.status),
  retracted: bool(record.retracted),
  createdAt: text(record.created_at),
  scheduledAt: text(record.scheduled_at),
  imageUrl: text(record.image_url),
  sentCount: num(record.sent_count),
  deliveredCount: num(record.delivered_count),
  openedCount: num(record.opened_count),
  // The click counter is `link_clicked_count`. There is no `clicked_count` on
  // a broadcast record — reading that name yields undefined, which decodes to
  // null, which renders "—" on a send that demonstrably had clicks.
  clickedCount: num(record.link_clicked_count),
});

/**
 * GET /api/v1/broadcasts → `{ broadcasts: [...] }`, with the older envelopes
 * still tolerated.
 *
 * @param {Object|Array} payload
 * @returns {{ rows: Object[], total: number|null }}
 */
export const listFromPayload = (payload) => ({
  rows: unwrapList(payload, "broadcasts").map((record) => rowFromRecord(record)),
  total: totalOf(payload),
});

/* ── Overview ─────────────────────────────────────────────────────────── */

/**
 * The analytics header. Every field is a counter or a rate, so every field is
 * null until the server has one.
 *
 * @param {Object} payload
 * @returns {Object}
 */
export const overviewFromPayload = (payload = {}) => ({
  broadcastsSent: num(payload.broadcasts_sent),
  scheduledAhead: num(payload.scheduled_ahead),
  uniqueReached: num(payload.unique_reached),
  uniquePush: num(payload.unique_push),
  uniqueEmail: num(payload.unique_email),
  uniqueOpeners: num(payload.unique_openers),
  uniqueClickers: num(payload.unique_clickers),
  openRatePct: num(payload.open_rate_pct),
  clickRatePct: num(payload.click_rate_pct),
});

/* ── Estimate ─────────────────────────────────────────────────────────── */

const pushEstimate = (raw) =>
  raw
    ? {
        segment: slug(raw.segment),
        audienceCount: num(raw.audience_count),
        reachable: num(raw.reachable),
        unreachable: num(raw.unreachable),
      }
    : null;

const emailEstimate = (raw) =>
  raw
    ? {
        segment: slug(raw.segment),
        audienceCount: num(raw.audience_count),
        reachable: num(raw.reachable),
        suppressed: num(raw.suppressed),
        noAddress: num(raw.no_address),
      }
    : null;

/**
 * POST /api/v1/broadcasts/estimate.
 *
 * A push-only estimate carries no `email` block and vice versa. The absent
 * block decodes to null, meaning "this channel is not targeted" — not zero,
 * which would mean "targeted, reaches nobody".
 *
 * CRITICAL: `totalSends` and `uniquePeople` are returned exactly as the server
 * gave them and are NEVER recomputed from the per-channel numbers. A user in
 * both audiences is two sends and one person; the server knows the overlap and
 * we do not. Adding `push.reachable + email.reachable` would silently overstate
 * unique reach by the size of that overlap, and would do it most on exactly the
 * broadcasts where reach matters most. `bothChannels` is the server's own
 * count of that overlap and is likewise passed straight through.
 *
 * @param {Object} payload
 * @returns {Object}
 */
export const estimateFromPayload = (payload = {}) => ({
  channels: channelsOf(payload.channels),
  push: pushEstimate(payload.push),
  email: emailEstimate(payload.email),
  totalSends: num(payload.total_sends),
  uniquePeople: num(payload.unique_people),
  bothChannels: num(payload.both_channels),
});

/* ── Funnel ───────────────────────────────────────────────────────────── */

/**
 * Read one channel out of `by_channel`, tolerating either casing of the key.
 * @returns {Object|null} null when the channel was never targeted.
 */
const channelStats = (byChannel, key) => {
  const raw = byChannel?.[key.toUpperCase()] || byChannel?.[key.toLowerCase()];
  if (!raw) return null;
  return {
    queued: num(raw.queued),
    sent: num(raw.sent),
    delivered: num(raw.delivered),
    opened: num(raw.opened),
    clicked: num(raw.clicked),
  };
};

/**
 * GET /api/v1/broadcasts/:id/funnel.
 *
 * @param {Object} payload
 * @returns {Object}
 */
export const funnelFromPayload = (payload = {}) => {
  const byChannel = payload.by_channel;
  const email = channelStats(byChannel, "email");
  const rawEmail = byChannel?.EMAIL || byChannel?.email;
  const combined = payload.combined;
  const notes = payload.notes;

  return {
    broadcast: payload.broadcast ? rowFromRecord(payload.broadcast) : null,
    // A push-only broadcast has no EMAIL key. It decodes to null so the UI can
    // hide the email column outright rather than draw an all-zero funnel that
    // reads as a total delivery failure.
    push: channelStats(byChannel, "push"),
    email: email
      ? {
          ...email,
          // Two terminal stages push has no equivalent for.
          suppressed: num(rawEmail.suppressed),
          bounced: num(rawEmail.bounced),
        }
      : null,
    combined: combined
      ? {
          uniqueRecipients: num(combined.unique_recipients),
          uniqueReached: num(combined.unique_reached),
          uniqueOpeners: num(combined.unique_openers),
          uniqueClickers: num(combined.unique_clickers),
          reachedOnBothChannels: num(combined.reached_on_both_channels),
          totalSends: num(combined.total_sends),
        }
      : null,
    // The API's own caveat that email opens are inflated by Gmail's image
    // proxy and Apple Mail Privacy Protection. Shown verbatim as a tooltip on
    // the opened cell — we do not paraphrase a correctness warning.
    notes: notes ? { opens: text(notes.opens) } : null,
  };
};

/* ── Recipients ───────────────────────────────────────────────────────── */

/**
 * One row of the recipients grid.
 *
 * Missing `email`/`phone` stay null rather than becoming "": the grid renders
 * "—" for null and would render an empty cell for the string.
 *
 * @param {Object} record
 * @returns {Object}
 */
const recipientFromRecord = (record = {}) => ({
  id: text(pick(record, ["user_id", "id"])),
  name: text(pick(record, ["name", "full_name"])),
  email: text(record.email),
  phone: text(record.phone),
  channel: slug(record.channel),
  state: slug(record.state),
  engagement: slug(record.engagement),
  lastEventAt: text(record.last_event_at),
});

/**
 * GET /api/v1/broadcasts/:id/recipients.
 *
 * @param {Object|Array} payload
 * @returns {{ recipients: Object[], total: number|null }}
 */
export const recipientsFromPayload = (payload) => ({
  recipients: unwrapList(payload, "recipients").map((record) =>
    recipientFromRecord(record)
  ),
  total: totalOf(payload),
});

/* ── Create / retract ─────────────────────────────────────────────────── */

/** The service's `post` verb resolves `{ data, status }`; tests pass bare data. */
const unwrapResponse = (response) =>
  response?.data !== undefined && response?.data !== null ? response.data : response;

/**
 * POST /api/v1/broadcasts.
 *
 * `enqueued` is the raw boolean, or null when the server did not send the key.
 * It is NOT defaulted to true. `enqueued: false` means the broadcast row was
 * created but delivery never started, and the composer shows an amber "Saved,
 * but delivery has not started" instead of the green success toast. Coercing a
 * missing key into true would turn "we don't know" into a claim we can't back,
 * on the one screen where the author walks away believing the send is running.
 *
 * @param {Object} response
 * @returns {{ id: string|null, success: boolean|null, enqueued: boolean|null }}
 */
export const createResultFromResponse = (response) => {
  const data = unwrapResponse(response) || {};
  const httpStatus = num(response?.status);
  const rawSuccess = pick(data, ["success", "ok"]);

  const success =
    rawSuccess !== undefined
      ? bool(rawSuccess)
      : httpStatus === null
      ? null
      : httpStatus >= 200 && httpStatus < 300;

  const rawEnqueued = data.enqueued;

  return {
    id: text(pick(data, ["id", "broadcast_id"])),
    success,
    enqueued:
      rawEnqueued === undefined || rawEnqueued === null ? null : bool(rawEnqueued),
  };
};

/**
 * POST /api/v1/broadcasts/:id/retract → a `note` stating plainly what
 * retraction cannot undo (pushes already on a device stay on it). Returned
 * verbatim; the UI must not rewrite a statement about what already happened.
 *
 * @param {Object} response
 * @returns {string|null}
 */
export const retractNoteFrom = (response) => text(unwrapResponse(response)?.note);

/**
 * Map a wire field name from a 422 onto the draft field the composer edits.
 *
 * A validation error names the field the SERVER rejected (`notification_title`),
 * but the composer's inputs are keyed by draft name (`title`). Without this
 * translation a 422 can only be shown as a page-level banner, which makes the
 * author hunt for which of six fields the server meant.
 *
 * Lives here because mapping *from* a wire name is exactly this module's job.
 * Unknown names return null so the caller can fall back to the banner rather
 * than attaching the message to an arbitrary input.
 *
 * ---------------------------------------------------------------------------
 * THE SAME WIRE FIELD LANDS ON TWO DIFFERENT INPUTS
 * ---------------------------------------------------------------------------
 * The composer builds either a popup or an email, and the content slot on the
 * wire is filled from whichever one it is. So a 422 on the headline field
 * belongs to the popup's Title input on a popup draft and to the email's
 * Subject input on an email one. Attaching it to `title` regardless would pin
 * the server's complaint to a field that is not on the screen — which is the
 * same as dropping it, only quieter.
 *
 * @param {string} wireName
 * @param {{ email?: boolean }} [mode] - Whether the draft being composed is the email one.
 * @returns {string|null} The draft field name, or null when unmapped.
 */
export const draftFieldFromWire = (wireName, { email = false } = {}) =>
  ({
    notification_title: email ? "emailSubject" : "title",
    // On an email `notification_body` is derived from the body, so a complaint
    // about it belongs on the field the author can actually edit.
    notification_body: email ? "emailBodyHtml" : "body",
    // Only the popup sends an image. On an email there is no input to pin a
    // complaint to, so it falls through to the page banner rather than being
    // attached to a field that is not on the screen.
    ...(email ? {} : { image_url: "imageUrl" }),
    email_subject: "emailSubject",
    email_body_html: "emailBodyHtml",
    email_cta_text: "emailCtaText",
    email_cta_url: "emailCtaUrl",
    route_key: "routeKey",
    channels: "channels",
    push_segment: "pushSegment",
    email_segment: "emailSegment",
    scheduled_at: "scheduledAtLocal",
  }[wireName] ?? null);

/**
 * Turn the `{wireField: message}` map from a 422 into the same
 * `[{field, message}]` array shape `validateDraft` produces, so the composer's
 * cards render server complaints through the identical path as local ones.
 *
 * Unmapped fields are dropped — the caller still shows the raw `detail` in the
 * banner, so nothing is lost by not guessing where to attach them.
 *
 * @param {Object} fieldErrors
 * @param {{ email?: boolean }} [mode] - Passed through to `draftFieldFromWire`.
 * @returns {Array<{ field: string, message: string }>}
 */
export const draftErrorsFromFieldErrors = (fieldErrors = {}, mode) =>
  Object.entries(fieldErrors).reduce((list, [wireName, message]) => {
    const field = draftFieldFromWire(wireName, mode);
    return field ? [...list, { field, message }] : list;
  }, []);
